import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Scissors, Watch, Glasses, Palette, User2, ArrowRight, RefreshCw } from "lucide-react";
import TopBar from "@/components/TopBar";
import Skeleton from "@/components/Skeleton";
import api, { formatApiError } from "@/lib/api";
import { useOutfitDraft } from "@/context/OutfitDraftContext";

function colorToCss(name) {
  if (!name) return "#888";
  const n = String(name).toLowerCase().trim();
  const map = {
    "black":"#0a0a0a","white":"#f5f5f5","ivory":"#f1ece1","cream":"#efe7d3",
    "beige":"#d6c3a5","tan":"#c8a778","camel":"#b58a5a","khaki":"#a08a5b",
    "olive":"#5b6034","navy":"#1c2540","blue":"#3a5aa8","denim":"#3e5b80",
    "gray":"#7a7a7a","grey":"#7a7a7a","charcoal":"#2c2c2c","stone":"#9b9485",
    "brown":"#5a3b22","chocolate":"#3b2418","burgundy":"#5a1a23","wine":"#5a1a23",
    "red":"#a83232","rust":"#a85533","orange":"#c66a2c","mustard":"#c69a2c",
    "yellow":"#d8c14b","green":"#365e3a","forest":"#1f3a26","sage":"#9bae8b",
    "teal":"#1e5d63","purple":"#52366b","pink":"#d59bb0",
  };
  for (const k of Object.keys(map)) if (n.includes(k)) return map[k];
  return "#888";
}

function AnalyzingSkeleton() {
  return (
    <div data-testid="appearance-loading" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "16px 0" }}>
        <div className="spinner" />
        <div className="tiny" style={{ letterSpacing: "0.22em", textTransform: "uppercase" }}>
          Analyzing your appearance…
        </div>
      </div>
      {[0,1,2,3,4].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton w="40%" h={10} />
          <Skeleton w="90%" h={14} />
        </div>
      ))}
    </div>
  );
}

function DetectedRow({ label, value, testId }) {
  if (!value) return null;
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12,
        padding: "10px 0", borderBottom: "1px solid var(--line)",
      }}
    >
      <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>
        {label}
      </div>
      <div className="body-sm" style={{ color: "#fff", textAlign: "right", maxWidth: "65%" }}>{value}</div>
    </div>
  );
}

function BulletList({ items, testIdPrefix }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="bullet-list" style={{ marginTop: 4 }}>
      {items.map((s, i) => (
        <li key={i} data-testid={`${testIdPrefix}-${i}`}>{s}</li>
      ))}
    </ul>
  );
}

export default function AppearanceScreen() {
  const navigate = useNavigate();
  const { draft, update } = useOutfitDraft();
  const ranRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appearance, setAppearance] = useState(draft.appearance || null);

  const analyze = async () => {
    if (!draft.imageBase64) {
      setError("Add a photo first.");
      setLoading(false);
      return;
    }
    setError("");
    try {
      const { data } = await api.post("/appearance/analyze", {
        image_base64: draft.imageBase64,
        image_mime: draft.imageMime || "image/jpeg",
      });
      setAppearance(data.appearance);
      const detected = data.appearance?.detected?.skin_tone;
      update({
        appearance: data.appearance,
        ...(detected && !draft.skinTone ? { skinTone: detected } : {}),
      });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!draft.imageBase64) {
      navigate("/upload?source=gallery", { replace: true });
      return;
    }
    if (draft.appearance) {
      setLoading(false);
      setAppearance(draft.appearance);
      return;
    }
    setLoading(true);
    analyze();
  }, []);

  const onReanalyze = () => {
    setLoading(true);
    analyze();
  };

  const detected = appearance?.detected || {};
  const rec = appearance?.recommendations || {};

  const next = () => navigate("/style-pick");

  return (
    <div className="screen fade-in" data-testid="appearance-screen">
      <TopBar
        title="STEP 2 OF 3"
        right={
          !loading && appearance ? (
            <button
              type="button"
              className="icon-btn"
              onClick={onReanalyze}
              aria-label="Re-analyze"
              data-testid="appearance-reanalyze-btn"
            >
              <RefreshCw size={16} strokeWidth={1.7} />
            </button>
          ) : null
        }
      />
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>YOUR LOOK</div>
        <h2 className="h1" style={{ fontSize: 34 }}>
          What we <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>see</em>
        </h2>
        <p className="body" style={{ marginTop: 12 }}>
          A quick read on your appearance — used to pick colors and shapes that flatter you.
        </p>
      </div>

      {draft.imageBase64 && (
        <img
          src={draft.imageBase64}
          alt="you"
          className="preview-img"
          style={{ maxHeight: 280, marginBottom: 18 }}
          data-testid="appearance-photo"
        />
      )}

      {loading && <AnalyzingSkeleton />}

      {error && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="appearance-error-state">
          <div className="eyebrow" style={{ color: "var(--danger)" }}>SOMETHING WENT WRONG</div>
          <p className="body" style={{ color: "#fff" }}>{error}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-outline" onClick={onReanalyze} data-testid="appearance-retry-btn">Try again</button>
            <button className="btn btn-ghost" onClick={() => navigate("/style-pick")} data-testid="appearance-skip-btn">
              Skip — pick style
            </button>
          </div>
        </div>
      )}

      {!loading && !error && appearance && (
        <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Detected */}
          <div className="outfit-section" data-testid="appearance-detected-section">
            <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <User2 size={12} strokeWidth={1.8} /> DETECTED
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <DetectedRow label="Hair color"      value={detected.hair_color}        testId="appearance-hair-color" />
              <DetectedRow label="Hair style"      value={detected.hair_style}        testId="appearance-hair-style" />
              <DetectedRow label="Facial hair"     value={detected.facial_hair}       testId="appearance-facial-hair" />
              <DetectedRow label="Face shape"      value={detected.face_shape}        testId="appearance-face-shape" />
              <DetectedRow label="Skin tone"       value={detected.skin_tone}         testId="appearance-skin-tone" />
              <DetectedRow label="Body"            value={detected.body_proportions}  testId="appearance-body" />
              <DetectedRow label="Overall"         value={detected.overall_appearance} testId="appearance-overall" />
            </div>
          </div>

          {/* Recommended colors */}
          {rec.clothing_colors?.length > 0 && (
            <div className="outfit-section" data-testid="appearance-colors-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Palette size={12} strokeWidth={1.8} /> COLORS THAT FLATTER YOU
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14, rowGap: 10 }}>
                {rec.clothing_colors.map((c, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid={`appearance-color-${i}`}>
                    <span className="color-dot" style={{ background: colorToCss(c) }} />
                    <span className="body-sm" style={{ color: "#fff" }}>{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hairstyles */}
          {rec.hairstyles?.length > 0 && (
            <div className="outfit-section" data-testid="appearance-hairstyles-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Scissors size={12} strokeWidth={1.8} /> HAIRSTYLES THAT FIT YOU
              </div>
              <BulletList items={rec.hairstyles} testIdPrefix="appearance-hairstyle" />
            </div>
          )}

          {/* Accessories */}
          {rec.accessories?.length > 0 && (
            <div className="outfit-section" data-testid="appearance-accessories-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Watch size={12} strokeWidth={1.8} /> ACCESSORIES FOR YOU
              </div>
              <BulletList items={rec.accessories} testIdPrefix="appearance-accessory" />
            </div>
          )}

          {/* Glasses (only if applicable) */}
          {rec.glasses?.length > 0 && rec.glasses[0] !== "not needed" && (
            <div className="outfit-section" data-testid="appearance-glasses-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Glasses size={12} strokeWidth={1.8} /> GLASSES
              </div>
              <BulletList items={rec.glasses} testIdPrefix="appearance-glasses" />
            </div>
          )}

          {/* Outfit directions */}
          {rec.outfit_directions?.length > 0 && (
            <div className="outfit-section" data-testid="appearance-outfit-directions-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Sparkles size={12} strokeWidth={1.8} /> OUTFIT DIRECTIONS
              </div>
              <BulletList items={rec.outfit_directions} testIdPrefix="appearance-direction" />
            </div>
          )}

          <div style={{ marginTop: 22 }}>
            <button className="btn btn-primary" onClick={next} data-testid="appearance-continue-btn">
              Choose a style <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
